import { ParameterValues, paramRepository } from '../../../core/resources';
import { AnnouncementTypeEnum, BovineSex, FarmingTypeEnum, SalesType } from '../../../interfaces/enums';
import { IAnimalAnnouncementAnimal } from '../../../interfaces/IAnnouncement';
import { IFarmDetails } from '../../../interfaces/IBusinessEntities';
import { getFarmingTypesFromFarmDetails } from './data';

export interface ISalesTypeParams {
    BreederBroker: ParameterValues;
    RearingUnitBroker: ParameterValues;
    DairyCattleBroker: ParameterValues;
    SucklerCowCattleBroker: ParameterValues;
    BreederSlaughter: ParameterValues;
    RearingUnitSlaughter: ParameterValues;
    DairyCattleSlaughter: ParameterValues;
    SucklerCowCattleSlaughter: ParameterValues;
}

export function getAllowedAnnouncementSalesTypes(
    farmDetails: IFarmDetails,
    announcementType?: AnnouncementTypeEnum
): Promise<ParameterValues> {
    return getSalesTypeParameters().then((params) => {
        return salesTypeParamsForFarmAndAnnouncementType(farmDetails, params, announcementType);
    });
}

export function filterSalesTypeParameter(param: ParameterValues) {
    param = param.filter((p) => !isDepricated(p.code));
    return param;
}

export function filterSalesTypeParameters(params: ISalesTypeParams) {
    params.BreederBroker = params.BreederBroker.filter((p) => !isDepricated(p.code));
    params.RearingUnitBroker = params.RearingUnitBroker.filter((p) => !isDepricated(p.code));
    params.DairyCattleBroker = params.DairyCattleBroker.filter((p) => !isDepricated(p.code));
    params.SucklerCowCattleBroker = params.SucklerCowCattleBroker.filter(
        (p) => !isDepricated(p.code)
    );
    params.BreederSlaughter = params.BreederSlaughter.filter((p) => !isDepricated(p.code));
    params.RearingUnitSlaughter = params.RearingUnitSlaughter.filter((p) => !isDepricated(p.code));
    params.DairyCattleSlaughter = params.DairyCattleSlaughter.filter((p) => !isDepricated(p.code));
    params.SucklerCowCattleSlaughter = params.SucklerCowCattleSlaughter.filter(
        (p) => !isDepricated(p.code)
    );
}

function isDepricated(salesTypeCode: string) {
    let parsed = Number.parseInt(salesTypeCode);

    if (isNaN(parsed)) return false;

    switch (parsed) {
        case 34:
        case 35:
        case 55:
        case 56:
            return true;
        default:
            return false;
    }
}

export function getSalesTypeParameters(): Promise<ISalesTypeParams> {
    return paramRepository.load(['AnelmaBovine']).then(() => {
        return {
            BreederBroker: paramRepository.resource('AnelmaBovine', 'BreederBrokerSale'),
            RearingUnitBroker: paramRepository.resource('AnelmaBovine', 'RearingUnitBrokerSale'),
            DairyCattleBroker: paramRepository.resource('AnelmaBovine', 'DairyCattleBrokerSale'),
            SucklerCowCattleBroker: paramRepository.resource(
                'AnelmaBovine',
                'SucklerCowCattleBrokerSale'
            ),
            BreederSlaughter: paramRepository.resource('AnelmaBovine', 'BreederSlaughterSale'),
            RearingUnitSlaughter: paramRepository.resource(
                'AnelmaBovine',
                'RearingUnitSlaughterSale'
            ),
            DairyCattleSlaughter: paramRepository.resource(
                'AnelmaBovine',
                'DairyCattleSlaughterSale'
            ),
            SucklerCowCattleSlaughter: paramRepository.resource(
                'AnelmaBovine',
                'SucklerCowCattleSlaughterSale'
            ),
        } as ISalesTypeParams;
    });
}

export function salesTypeParamsForFarmAndAnnouncementType(
    farmDetails: IFarmDetails,
    params: ISalesTypeParams,
    announcementType?: AnnouncementTypeEnum
): ParameterValues {
    const parameterValues: ParameterValues = [];

    const add = (values: ParameterValues) => {
        for (let v of values) {
            if (!parameterValues.find((x) => x.code == v.code)) parameterValues.push(v);
        }
    };

    // Jos tilalla on useampi tuotantosuunta, listaan yhdistetään tilan tuotantosuuntien parametrit
    const farmingTypesInCurrentFarm = getFarmingTypesFromFarmDetails(farmDetails.ProductionLines);

    for (const farmingType of farmingTypesInCurrentFarm) {
        if ( announcementType === AnnouncementTypeEnum.Slaughter ||
            announcementType === AnnouncementTypeEnum.ManualBovineSlaughter || 
            announcementType === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) {
            switch (farmingType) {
                case FarmingTypeEnum.Calving: // Emolehmätila
                    add(params.SucklerCowCattleSlaughter);
                    break;
                case FarmingTypeEnum.Dairy: // Lypsykarjatila
                    add(params.DairyCattleSlaughter);
                    break;
                case FarmingTypeEnum.Upbringing: // Kasvattaja
                    add(params.BreederSlaughter);
                    break;
                case FarmingTypeEnum.Transmission: // Välikasvattamo
                    add(params.RearingUnitSlaughter);
                    break;
                default:
                    break;
            }
        } else if (
            announcementType === AnnouncementTypeEnum.Intermediation ||
            announcementType === AnnouncementTypeEnum.ManualBovineIntermediation || 
            announcementType === AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) {
            switch (farmingType) {
                case FarmingTypeEnum.Calving: // Emolehmätila
                    add(params.SucklerCowCattleBroker);
                    break;
                case FarmingTypeEnum.Dairy: // Lypsykarjatila
                    add(params.DairyCattleBroker);
                    break;
                case FarmingTypeEnum.Upbringing: // Kasvattaja
                    add(params.BreederBroker);
                    break;
                case FarmingTypeEnum.Transmission: // Välikasvattamo
                    add(params.RearingUnitBroker);
                    break;
                default:
                    break;
            }
        } else if (announcementType === undefined) {
            switch (farmingType) {
                case FarmingTypeEnum.Calving: // Emolehmätila
                    add([...params.SucklerCowCattleSlaughter, ...params.SucklerCowCattleBroker]);
                    break;
                case FarmingTypeEnum.Dairy: // Lypsykarjatila
                    add([...params.DairyCattleSlaughter, ...params.DairyCattleBroker]);
                    break;
                case FarmingTypeEnum.Upbringing: // Kasvattaja
                    add([...params.BreederSlaughter, ...params.BreederBroker]);
                    break;
                case FarmingTypeEnum.Transmission: // Välikasvattamo
                    add([...params.RearingUnitSlaughter, ...params.RearingUnitBroker]);
                    break;
                default:
                    break;
            }
        }
    }
    return parameterValues;
}

// Rules for eligiblity taken from AN-2169 ticket
// 1) If bovine sex is 0, announcement type is slaughter and sales type Sonni, LimSonniAlaCarte, Pihvisonni or Pihvihieho return true
// 2) If bovine sex is 1 and WasBreeding is true and announcement type is slaughter and sales type is Lehmä or Pikalehmä, return true
// 3) If bovine sex is 1 and WasBreeding is null or false and announcement type is slaughter and sales type is Hieho or LimHiehoAlaCarte, return true
// 4) If bovine sex is 0, announcement type is intermediation and sales type is PihSovas75prossa, LimSoVasAlaCarte, Sonnivasikka, Ternisonnivas, VälikasvSoVas or Siitossonni, return true
// 5) If bovine sex is 1 and WasBreeding is true and announcement type is intermediation and sales type is Siitoslehmä, return true
// 6) If bovine sex is 1 and WasBreeding is null or false and announcement type is intermediation and sales type is PihLevas75prossa, LimLeVasAlaCarte, Lehmävasikka, Ternilehmävas, VälikasvLeVas, KantavaSiitoshieho or Siitoshieho, return true
// 7) In all other cases, return false
export const isSalesTypeEligibleForAnnouncement = (
    animals: IAnimalAnnouncementAnimal[],
    salesType: number | string,
    announcementType?: AnnouncementTypeEnum
): boolean => {
    // Coerce once, sales type has to be type of number for comparisons if not return false
    const salesTypeNumber = typeof salesType === 'number' ? salesType : Number(salesType);
    if (Number.isNaN(salesTypeNumber)) return false;

    // If no animals provided we can't determine sex/breeding status => false per rule 7
    if (!animals || animals.length === 0) return false;

    // Evaluate animal-by-animal; all animals must satisfy eligibility for provided salesType.
    // Breeding status considered per animal; undefined treated as non-breeding.
    switch (announcementType) {
        case AnnouncementTypeEnum.Slaughter:
        case AnnouncementTypeEnum.SlaughterWithChainInformation:
        case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement: {
            return animals.every((a) => {
                const sex = a.Sex;
                if (sex === 0) {
                    return SLAUGHTER_BULL_TYPES.has(salesTypeNumber); // Rule 1
                } else if (sex === 1) {
                    return a.WasBreeding === true
                        ? SLAUGHTER_COW_BREEDING_TYPES.has(salesTypeNumber) // Rule 2
                        : SLAUGHTER_COW_NON_BREEDING_TYPES.has(salesTypeNumber); // Rule 3
                }
                return false; // Unknown sex
            });
        }
        case AnnouncementTypeEnum.ManualBovineSlaughter: {
            // Updated assumption: mixture of bulls and cows allowed; cows are always considered breeding (Rule 2),
            // so valid sales types are bull slaughter types OR breeding-cow slaughter types.
            return (
                animals.length > 0 &&
                animals.every((a) => {
                    const sex = a.Sex;
                    if (sex === 0) return SLAUGHTER_BULL_TYPES.has(salesTypeNumber); // Rule 1 subset
                    if (sex === 1) return SLAUGHTER_COW_BREEDING_TYPES.has(salesTypeNumber); // Rule 2 only
                    return false;
                })
            );
        }
        case AnnouncementTypeEnum.Intermediation:
        case AnnouncementTypeEnum.ManualBovineIntermediation:
        case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement: {
            return animals.every((a) => {
                const sex = a.Sex;
                if (sex === 0) {
                    return INTERMEDIATION_BULL_TYPES.has(salesTypeNumber); // Rule 4
                } else if (sex === 1) {
                    return a.WasBreeding === true
                        ? INTERMEDIATION_COW_BREEDING_TYPES.has(salesTypeNumber) // Rule 5
                        : INTERMEDIATION_COW_NON_BREEDING_TYPES.has(salesTypeNumber); // Rule 6
                }
                return false;
            });
        }
        default:
            return false; // Rule 7
    }
};

export const isSalesTypeEligible = (
    animals: IAnimalAnnouncementAnimal[],
    salesType: number | string
): boolean => {
    // Coerce once; if invalid number return false
    const salesTypeNumber = typeof salesType === 'number' ? salesType : Number(salesType);
    if (Number.isNaN(salesTypeNumber)) return false;

    // Need animals to evaluate sex/breeding status
    if (!animals || animals.length === 0) return false;

    // This variant does NOT consider announcement type. A sales type is considered eligible
    // for the given animals if for every animal its sex & breeding status maps to one of the
    // combined allowable sets (animal list, slaughter & intermediation) defined by rules 1-6 in AN-2169 (later extended).
    return animals.every((a) => {
        const sex = (a.Sex = a.Sex);
        if (sex === 0) {
            return (
                SLAUGHTER_BULL_TYPES.has(salesTypeNumber) ||
                INTERMEDIATION_BULL_TYPES.has(salesTypeNumber)
            );
        } else if (sex === 1) {
            const isBreeding = a.WasBreeding === true;
            if (isBreeding) {
                return (
                    ANIMAL_LIST_COW_BREEDING_TYPES.has(salesTypeNumber) ||
                    INTERMEDIATION_COW_BREEDING_TYPES.has(salesTypeNumber)
                );
            } else {
                return (
                    SLAUGHTER_COW_NON_BREEDING_TYPES.has(salesTypeNumber) ||
                    INTERMEDIATION_COW_NON_BREEDING_TYPES.has(salesTypeNumber)
                );
            }
        }
        return false; // Unknown sex
    });
};

export const defaultSalesType = (announcementType: AnnouncementTypeEnum, bovineSex: BovineSex) => {
    switch (announcementType) {
        case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
            switch (bovineSex) {
                case BovineSex.Bull:
                    return SalesType.Sonni;
                case BovineSex.Cow:
                    return SalesType.Hieho;
                default:
                    return SalesType.Undefined;
            }
        case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
            switch (bovineSex) {
                case BovineSex.Bull:
                    return SalesType.VälikasvSoVas;
                case BovineSex.Cow:
                    return SalesType.VälikasvLeVas;
                default:
                    return SalesType.Undefined;
            }
        case AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement:
            switch (bovineSex) {
                case BovineSex.Bull:
                    return SalesType.Ternisonnivas;
                case BovineSex.Cow:
                    return SalesType.Ternilehmavas;
                default:
                    return SalesType.Undefined;
            }
        default:
            return SalesType.Undefined;
    }
}

// Exported rule sets for reuse elsewhere (e.g., UI validation or filtering)
export const ANIMAL_LIST_COW_BREEDING_TYPES = new Set<number>([
    SalesType.Lehmä,
    SalesType.Pikalehmä,
    SalesType.Lehmävasikka,
]);

export const SLAUGHTER_BULL_TYPES = new Set<number>([
    SalesType.Sonni,
    SalesType.LimSonniAlaCarte,
    SalesType.Pihvisonni,
]);
export const SLAUGHTER_COW_BREEDING_TYPES = new Set<number>([SalesType.Lehmä, SalesType.Pikalehmä]);
export const SLAUGHTER_COW_NON_BREEDING_TYPES = new Set<number>([
    SalesType.Hieho,
    SalesType.LimHiehoAlaCarte,
    SalesType.Pihvihieho,
]);

export const INTERMEDIATION_BULL_TYPES = new Set<number>([
    SalesType.PihSovas75prossa,
    SalesType.LimSoVasAlaCarte,
    SalesType.Sonnivasikka,
    SalesType.Ternisonnivas,
    SalesType.VälikasvSoVas,
    SalesType.Siitossonni,
]);
export const INTERMEDIATION_COW_BREEDING_TYPES = new Set<number>([SalesType.Siitoslehmä]);
export const INTERMEDIATION_COW_NON_BREEDING_TYPES = new Set<number>([
    SalesType.PihLevas75prossa,
    SalesType.LimLeVasAlaCarte,
    SalesType.Lehmävasikka,
    SalesType.Ternilehmavas,
    SalesType.VälikasvLeVas,
    SalesType.KantavaSiitoshieho,
    SalesType.Siitoshieho,
]);
